import { useState, useEffect } from "react";
import { Badge, Card } from "@weretradeit/ui-react";

interface SmtpLogEntry {
  id: string;
  subject: string;
  to: string;
  from: string;
  status: "sent" | "queued" | "failed";
  provider: string;
  timestamp: string;
  error?: string;
}

export const SmtpLogsWidget = () => {
  const [logs, setLogs] = useState<SmtpLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const response = await fetch("/logging/api/smtp", {
          credentials: "include"
        });
        if (!response.ok) {
          throw new Error(`Request failed with ${response.status}`);
        }
        const payload = await response.json();
        const data = payload.data ?? payload;
        // Show only the 5 most recent logs
        setLogs(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
    // Refresh every 30 seconds
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card title="SMTP Logs" className="log-widget">
      <p style={{ marginTop: 0, marginBottom: 12 }}>Recent outbound email events from Brevo/SMTP flows.</p>
      {loading && <p>Loading SMTP logs...</p>}
      {error && <Badge tone="warn">Failed to load: {error}</Badge>}
      {!loading && !error && logs.length === 0 && <p>No recent SMTP logs.</p>}
      {!loading && !error && logs.length > 0 && (
        <table className="token-grid logs-table" style={{ fontSize: "0.9em" }}>
          <thead>
            <tr>
              <th>Subject</th>
              <th>To</th>
              <th>Status</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((entry) => (
              <tr key={entry.id}>
                <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {entry.subject}
                </td>
                <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {entry.to}
                </td>
                <td>
                  <Badge tone={entry.status === "sent" ? "success" : entry.status === "queued" ? "info" : "warn"}>
                    {entry.status}
                  </Badge>
                </td>
                <td style={{ fontSize: "0.85em" }}>
                  {new Date(entry.timestamp).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <a href="/logging" style={{ fontSize: "0.9em", color: "var(--accent)" }}>View all SMTP logs →</a>
      </div>
    </Card>
  );
};
